import { ArbeitsbereichStation } from '@/domain/mitarbeiter/features/rotationsplan/entities/Station';
import { ArbeitsbereichOption } from '@/domain/mitarbeiter/features/rotationsplan/entities/ArbeitsbereichOption';
import { useRotationsplanById } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useRotationsplanById/useRotationsplanById';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router-dom';
import { UPDATE_FORM_ID } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/constants';
import { StandortSelect } from '@/domain/mitarbeiter/features/rotationsplan/features/CreateStation/components/StandortSelect';
import { Suspense } from 'react';
import { ArbeitsbereichSelect } from '@/domain/mitarbeiter/features/rotationsplan/features/CreateStation/components/ArbeitsbereichSelect';
import { hasUeberschneidungen } from '@/domain/mitarbeiter/features/rotationsplan/features/CreateStation/utils/hasUeberschneidungen';
import {
  Body,
  Button,
  FieldError,
  SheetBody,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Skeleton,
  Switch,
} from '@e2n/ui/v2';
import { DeleteArbeitsbereichStation } from '@/domain/mitarbeiter/features/rotationsplan/features/DeleteArbeitsbereichStation/DeleteArbeitsbereichStation';
import { AbteilungsleiterSelect } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/components/AbteilungsleiterSelect/AbteilungsleiterSelect';
import { FieldBase } from '@/domain/mitarbeiter/shared/components/form/fieldComponents/FieldBase';
import { useForm } from '@/domain/mitarbeiter/features/rotationsplan/features/UpdateStation/useForm';
import { useRegisterSheetForm } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/components/SheetWrapper/useRegisterSheetForm';
import { ErinnerungsZeitenMultiSelectV2 } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/components/ErinnerungsZeitenMultiSelectV2';
import { AbteilungsleiterMultiSelect } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/components/AbteilungsleiterMultiSelect/AbteilungsleiterMultiSelect';
import dayjs from 'dayjs';

export function UpdateStation() {
  const { t } = useTranslation();
  const { id, abschnittId, stationId } = useParams();
  const { currentStandort } = useStandortContext();
  const { rotationsplan } = useRotationsplanById(id ?? '');

  if (!rotationsplan || !abschnittId) {
    return <Navigate to="/mitarbeiter/rotationsplaene" />;
  }

  const station = rotationsplan.allStationen
    .filter((station) => station instanceof ArbeitsbereichStation)
    .find((station) => station.id === stationId);

  if (!station) {
    return <Navigate to={`/mitarbeiter/rotationsplan/${rotationsplan.id}`} />;
  }

  const form = useForm({ abschnittId, rotationsplan, station });

  useRegisterSheetForm(form);

  return (
    <div className="flex h-full flex-col bg-backgrounds-secondary">
      <SheetHeader
        actions={
          <>
            <DeleteArbeitsbereichStation station={station} />
            <Button form={UPDATE_FORM_ID}>{t('actions.fertig')}</Button>
          </>
        }>
        <SheetTitle className="flex-1">
          {t(
            'domain.mitarbeiter.rotationsplan.rotationsplaene.updateStation.title',
          )}
        </SheetTitle>
      </SheetHeader>
      <SheetBody className="flex flex-col gap-4 md:px-6 grow overflow-auto">
        <Body size="sm" className="text-labels-secondary">
          {t(
            'domain.mitarbeiter.rotationsplan.rotationsplaene.hinweisPflichtfelder',
          )}
        </Body>
        <form
          id={UPDATE_FORM_ID}
          className="flex flex-col gap-4 h-full"
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}>
          <form.AppField
            name="standort"
            listeners={{
              onChange: () =>
                form.setFieldValue(
                  'arbeitsbereich',
                  undefined as unknown as ArbeitsbereichOption,
                ),
            }}>
            {(field) => (
              <FieldBase
                isRequired
                label={t(
                  'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.standort.label',
                )}>
                <StandortSelect
                  handleChange={field.handleChange}
                  value={field.state.value}
                  land={rotationsplan.land}
                />
              </FieldBase>
            )}
          </form.AppField>
          <form.AppField
            name="arbeitsbereich"
            listeners={{
              onChange: () => form.setFieldValue('abteilungsleiter', undefined),
            }}>
            {(field) => (
              <FieldBase
                isRequired
                label={t(
                  'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.arbeitsbereich.label',
                )}>
                <Suspense fallback={<Skeleton className="w-108 h-10" />}>
                  <ArbeitsbereichSelect
                    value={field.state.value}
                    standortId={
                      form.getFieldValue('standort.id') ?? currentStandort.id
                    }
                    handleChange={field.handleChange}
                    mitarbeiter={rotationsplan.mitarbeiter}
                  />
                </Suspense>
              </FieldBase>
            )}
          </form.AppField>
          <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-3">
            <form.AppField
              name="von"
              validators={{
                onChange: ({ value, fieldApi }) => {
                  const bis = fieldApi.form.getFieldValue('bis');

                  if (dayjs(value).isAfter(bis)) {
                    return {
                      message: t(
                        'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.endeVorBeginn',
                      ),
                    };
                  }
                },
              }}
              listeners={{
                onChange: () => {
                  form.setFieldValue('erinnerungsZeit', []);
                },
              }}>
              {(field) => (
                <field.DatePickerInput
                  valueFormat="date"
                  isRequired
                  label={t(
                    'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.von.label',
                  )}
                />
              )}
            </form.AppField>
            <form.AppField
              name="bis"
              listeners={{
                onChange: () => {
                  form.validateField('von', 'change');
                },
              }}>
              {(field) => (
                <field.DatePickerInput
                  valueFormat="date"
                  isRequired
                  label={t(
                    'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.bis.label',
                  )}
                />
              )}
            </form.AppField>
          </div>
          <form.Subscribe
            selector={(state) => ({
              von: state.values.von,
              bis: state.values.bis,
            })}>
            {({ von, bis }) =>
              von <= bis &&
              hasUeberschneidungen(
                rotationsplan.allStationen,
                station,
                von,
                bis,
              ) ? (
                <FieldError
                  variant="warning"
                  errors={[
                    {
                      message: t(
                        'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.hasUeberschneidung',
                      ),
                    },
                  ]}
                />
              ) : null
            }
          </form.Subscribe>
          <form.AppField name="lernziele">
            {(field) => (
              <field.TextAreaInput
                label={t(
                  'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.lernziele.label',
                )}
              />
            )}
          </form.AppField>
          <form.AppField
            name="abteilungsleiter"
            listeners={{
              onChange: ({ value }) => {
                form.setFieldValue('erinnerungAn', value?.id ? [value.id] : []);
              },
            }}>
            {(field) => (
              <FieldBase
                label={t(
                  'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.abteilungsleitung.label',
                )}
                infoText={t(
                  'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.abteilungsleitung.tooltip',
                )}>
                <Suspense fallback={<Skeleton className="w-108 h-10" />}>
                  <form.Subscribe
                    selector={(state) => state.values.arbeitsbereich?.id}>
                    {(selectedArbeitsbereichId) => (
                      <AbteilungsleiterSelect
                        selectedArbeitsbereichId={selectedArbeitsbereichId}
                        onChange={field.handleChange}
                        selectedAbteilungsleiter={field.state.value}
                      />
                    )}
                  </form.Subscribe>
                </Suspense>
              </FieldBase>
            )}
          </form.AppField>
          <form.Subscribe selector={(state) => state.values.abteilungsleiter}>
            {(abteilungsleiter) => (
              <form.AppField
                listeners={{
                  onChange: ({ value }) => {
                    if (!value) {
                      form.setFieldValue('erinnerungsZeit', []);
                      form.setFieldValue(
                        'erinnerungAn',
                        abteilungsleiter?.id ? [abteilungsleiter.id] : [],
                      );
                    }
                  },
                }}
                name="fristErinnerung">
                {(field) => (
                  <div className="flex flex-col gap-4">
                    <Switch
                      id={field.name}
                      checked={field.state.value}
                      onCheckedChange={(value) => {
                        field.handleChange(value);
                      }}
                      label={t(
                        'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.fristErinnerung',
                      )}
                    />
                    {field.state.value && station.frist && (
                      <Body size="sm" className="text-labels-secondary">
                        {t(
                          'domain.mitarbeiter.rotationsplan.rotationsplaene.updateStation.fristHinweis',
                        )}
                      </Body>
                    )}
                  </div>
                )}
              </form.AppField>
            )}
          </form.Subscribe>
          <form.Subscribe
            selector={(state) => ({
              fristErinnerung: state.values.fristErinnerung,
              von: state.values.von,
              arbeitsbereichId: state.values.arbeitsbereich?.id,
              abteilungsleiterId: state.values.abteilungsleiter?.id,
            })}>
            {({ fristErinnerung, von, arbeitsbereichId }) =>
              fristErinnerung && (
                <>
                  <form.AppField name="erinnerungsZeit">
                    {(field) => (
                      <FieldBase
                        label={t(
                          'domain.mitarbeiter.fristen.erinnerungsZeit.label',
                        )}
                        isRequired>
                        <ErinnerungsZeitenMultiSelectV2
                          values={field.state.value}
                          handleChange={(value) => field.handleChange(value)}
                          fristdatum={von?.toISOString()}
                        />
                      </FieldBase>
                    )}
                  </form.AppField>
                  <form.AppField name="erinnerungAn">
                    {(field) => (
                      <FieldBase
                        label={t(
                          'domain.mitarbeiter.create.steps.anstellungsverhaeltnis.fristen.form.erinnerungAn.label',
                        )}
                        isRequired>
                        <Suspense
                          fallback={<Skeleton className="w-108 h-10" />}>
                          <AbteilungsleiterMultiSelect
                            values={field.state.value}
                            handleChange={(value) => field.handleChange(value)}
                            arbeitsbereichId={arbeitsbereichId}
                          />
                        </Suspense>
                      </FieldBase>
                    )}
                  </form.AppField>
                  <div className="h-1 shrink-0" aria-hidden />
                </>
              )
            }
          </form.Subscribe>
        </form>
      </SheetBody>
      <SheetFooter>
        <DeleteArbeitsbereichStation station={station} />
        <Button
          disabled={!form.state.isValid || form.state.isSubmitting}
          form={UPDATE_FORM_ID}>
          {t('actions.fertig')}
        </Button>
      </SheetFooter>
    </div>
  );
}
